import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, InlineNotification } from '@carbon/react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { BlockSkeleton, ErrorState, InlineError } from './states';

type Data = { lobbies: { key: string; label: string; off: boolean }[]; games: { game: string; lobby: string; off: boolean }[] };

// Commission on/off per lobby group and per game (agents.commissionOff*). Ticked = commission on.
export function CommissionSwitch() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['commission-switch'], queryFn: () => api.get<Data>('/api/admin/agents/commission-switch') });
  const [offGames, setOffGames] = useState<string[]>([]);
  const [offLobbies, setOffLobbies] = useState<string[]>([]);
  useEffect(() => {
    if (!q.data) return;
    setOffGames(q.data.games.filter((g) => g.off).map((g) => g.game));
    setOffLobbies(q.data.lobbies.filter((l) => l.off).map((l) => l.key));
  }, [q.data]);
  const m = useMutation({
    mutationFn: () => api.put('/api/admin/agents/commission-switch', { offGames, offLobbies }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commission-switch'] }),
  });
  if (q.isLoading) return <BlockSkeleton lines={6} />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const edit = can('agents.manage');
  const flip = (list: string[], set: (v: string[]) => void, v: string, on: boolean) => set(on ? list.filter((x) => x !== v) : [...list, v]);
  const saved = JSON.stringify([q.data.games.filter((g) => g.off).map((g) => g.game).sort(), q.data.lobbies.filter((l) => l.off).map((l) => l.key).sort()]);
  const dirty = saved !== JSON.stringify([[...offGames].sort(), [...offLobbies].sort()]);
  return (
    <section className="section">
      <h2>Hoa hồng theo sảnh và game</h2>
      <p className="muted">Bỏ chọn một sảnh hoặc một game thì cược ở đó không sinh hoa hồng cho đại lý. Áp dụng cho cược từ lúc lưu; hoa hồng đã tính giữ nguyên.</p>
      <div className="stack">
        {q.data.lobbies.map((l) => {
          const lobbyOn = !offLobbies.includes(l.key);
          const games = q.data.games.filter((g) => g.lobby === l.key);
          return (
            <fieldset key={l.key} className="stack" style={{ border: 0, padding: 0, margin: 0 }}>
              <Checkbox id={'lb-' + l.key} labelText={<strong>{l.label}</strong>} checked={lobbyOn} disabled={!edit}
                onChange={(_e, { checked }) => flip(offLobbies, setOffLobbies, l.key, checked)} />
              <div className="row" style={{ flexWrap: 'wrap', gap: 16, paddingLeft: 24 }}>
                {games.map((g) => (
                  <Checkbox key={g.game} id={'gm-' + g.game} labelText={g.game} disabled={!edit || !lobbyOn}
                    checked={lobbyOn && !offGames.includes(g.game)}
                    onChange={(_e, { checked }) => flip(offGames, setOffGames, g.game, checked)} />
                ))}
                {games.length ? null : <span className="muted">Chưa có game nào</span>}
              </div>
            </fieldset>
          );
        })}
        {edit ? (
          <div className="row">
            <Button size="sm" kind="tertiary" disabled={!dirty || m.isPending} onClick={() => m.mutate()}>Lưu</Button>
            {m.isSuccess && !dirty ? <InlineNotification kind="success" lowContrast hideCloseButton title="Đã lưu" /> : null}
          </div>
        ) : null}
        <InlineError error={m.error} />
      </div>
    </section>
  );
}
